import { XMLBuilder, XMLParser, XMLValidator } from 'fast-xml-parser';
import Papa from 'papaparse';
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import { EngineFailure } from '../../core/errors';

/** 结构化文本的解析/序列化原语，全部为纯函数，便于单测。 */

export interface CsvParseResult {
  rows: Array<Record<string, string>>;
  fields: string[];
}

export function parseDelimited(text: string, delimiter?: string): CsvParseResult {
  const result = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    delimiter,
  });
  const fatal = result.errors.filter((error) => error.code !== 'UndetectableDelimiter');
  if (fatal.length > 0) {
    throw new EngineFailure('MALFORMED_FILE', `分隔符文本解析失败：${fatal[0].message}`);
  }
  const rows = (result.data ?? []).filter((row) => row && Object.keys(row).length > 0);
  if (rows.length === 0) throw new EngineFailure('MALFORMED_FILE', '没有解析到任何数据行');
  const fields = (result.meta?.fields ?? Object.keys(rows[0] ?? {})).filter((field) => field && field.length > 0);
  return { rows, fields };
}

export function parseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch (error) {
    throw new EngineFailure('MALFORMED_FILE', `JSON 解析失败：${error instanceof Error ? error.message : String(error)}`);
  }
}

export function parseYamlText(text: string): unknown {
  try {
    const value = parseYaml(text) as unknown;
    if (value === undefined) throw new Error('内容为空');
    return value;
  } catch (error) {
    throw new EngineFailure('MALFORMED_FILE', `YAML 解析失败：${error instanceof Error ? error.message : String(error)}`);
  }
}

export function parseXmlText(text: string): unknown {
  const validation = XMLValidator.validate(text);
  if (validation !== true) {
    throw new EngineFailure('MALFORMED_FILE', `XML 结构不合法：${validation.err?.msg ?? '未知错误'}`);
  }
  const parser = new XMLParser({ ignoreAttributes: false, attributeNamePrefix: '@_', parseTagValue: true, trimValues: true });
  return parser.parse(text) as unknown;
}

export function isScalar(value: unknown): boolean {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value);
}

/** 嵌套对象按点号路径展平，数组以分号拼接——这一步必然有损，保真度会如实降级。 */
export function flattenRecord(record: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const walk = (value: unknown, prefix: string): void => {
    if (isScalar(value)) {
      out[prefix] = value;
      return;
    }
    if (Array.isArray(value)) {
      if (value.every(isScalar)) {
        out[prefix] = value.map((item) => String(item)).join(';');
        return;
      }
      value.forEach((item, index) => walk(item, `${prefix}[${index}]`));
      return;
    }
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      walk(nested, prefix ? `${prefix}.${key}` : key);
    }
  };
  walk(record, '');
  return out;
}

export interface DelimitedResult {
  csv: string;
  fields: string[];
  structurePreserved: boolean;
}

export function jsonToDelimited(value: unknown, delimiter = ','): DelimitedResult {
  const list = Array.isArray(value) ? value : [value];
  if (list.length === 0) throw new EngineFailure('MALFORMED_FILE', 'JSON 数组为空，无法生成表格');

  let structurePreserved = true;
  const rows: Array<Record<string, unknown>> = list.map((item) => {
    if (item === null || typeof item !== 'object' || Array.isArray(item)) {
      structurePreserved = false;
      return { value: item as unknown };
    }
    const record = item as Record<string, unknown>;
    const flat = flattenRecord(record);
    const flatKeys = Object.keys(flat);
    const nestedIntroduced = flatKeys.some((key) => key.includes('.') || key.includes('['));
    if (nestedIntroduced || flatKeys.length !== Object.keys(record).length) structurePreserved = false;
    return flat;
  });

  const csv = Papa.unparse(rows, { delimiter, newline: '\n' });
  const fields = Papa.parse<Record<string, string>>(csv, { header: true, skipEmptyLines: true }).meta?.fields ?? [];
  return { csv: csv.endsWith('\n') ? csv : `${csv}\n`, fields, structurePreserved };
}

export function jsonToYaml(value: unknown): string {
  return stringifyYaml(value, { lineWidth: 0 });
}

/** JSON → XML 的映射约定：单键对象以该键为根节点，其余内容包在 <root> 下。 */
export function jsonToXml(value: unknown): string {
  const wrapped = buildXmlRoot(value);
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    format: true,
    suppressEmptyNode: false,
  });
  const xml = builder.build(wrapped);
  return xml.endsWith('\n') ? xml : `${xml}\n`;
}

function buildXmlRoot(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 1) return { [entries[0][0]]: entries[0][1] };
  }
  if (Array.isArray(value)) return { root: { item: value } };
  return { root: value as unknown as Record<string, unknown> };
}

export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b));
  return `{${entries.map(([key, nested]) => `${JSON.stringify(key)}:${stableStringify(nested)}`).join(',')}}`;
}
